import React, {useState, useContext, useCallback, useEffect, useRef} from 'react'
import {SearchInput, withInputContainer} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {FormInput, FormDataContext} from '@modifi/plugin-forms'
import {
  InputPureWithInputContainer,
  TRADE_PARTNER_COMPANY_NAME,
  TRADE_PARTNER_STREET_NAME,
  TRADE_PARTNER_CITY,
  TRADE_PARTNER_STATE,
  TRADE_PARTNER_ZIP_CODE,
  TRADE_PARTNER_STREET_NUMBER,
  TRADE_PARTNER_COUNTRY_CODE,
  TRADE_PARTNER_LEGAL_FORM,
  TRADE_PARTNER_WEBSITE,
  TRADE_PARTNER_EXPECTED_FINANCING_LIMIT,
  TRADE_PARTNER_TRADING_CURRENCY,
} from '@modifi/plugin-onboarding'

import Suggestion from './Suggestion'
import {CompanyLabelWrapper, AutoSuggestWrapper, StyledButtonLink} from './styles'
import {SuggestionContainerFactory} from './SuggestionContainerFactory'
import {FormChangeContext, SelectedCountryContext, SelectedStateContext} from '../../context'

const SearchInputWithContainer = withInputContainer(SearchInput)

const minCharacterSearch = 4

type CompanyNameSearchProps = {
  onSearch: (
    query: string,
    countryCode: string,
    state?: string
  ) => Promise<Record<string, unknown>[]>
  getLegalForms: (countryCode: string) => string[]
  highBuyerRiskCountries?: string[]
  openBuyerSearchDialog: (params: Record<string, unknown>) => void
}

const CompanyNameSearch: React.FC<CompanyNameSearchProps> = ({
  onSearch,
  getLegalForms,
  highBuyerRiskCountries = [],
  openBuyerSearchDialog,
  ...restProps
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [selectedCompany, setSelectedCompany] = useState(false)
  const {onChange, discardChangeCounter} = useContext(FormChangeContext)
  const selectedCountryCode = useContext(SelectedCountryContext)
  const preselectedState = useContext(SelectedStateContext)
  const {changeData, data} = useContext(FormDataContext)
  const countryRef = useRef(selectedCountryCode)
  const [companySearchError, setCompanySearchError] = useState('')
  const [hasResults, setHasResults] = useState(true)

  const loadNewOptions = useCallback(
    async (inputValue: string) => {
      if (inputValue.length >= minCharacterSearch) {
        setHasResults(true)
        try {
          const results = await onSearch(
            inputValue,
            data?.[TRADE_PARTNER_COUNTRY_CODE] as string,
            preselectedState as string
          )
          if (!results.length) setHasResults(false)
          return results
        } catch (err) {
          // @ts-expect-error
          const {httpStatus, errorCode, errorMessage} = err?.data()
          let updatedErrorMessage = errorMessage
          if (httpStatus === 500) {
            updatedErrorMessage = t('trade-partner.company-name-search.error.500')
          } else if (httpStatus === 400 && errorCode) {
            updatedErrorMessage = t(`trade-partner.company-name-search.error.${errorCode}`)
          }
          setCompanySearchError(updatedErrorMessage)
        }
      }
      return new Promise(resolve => resolve([]))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSearch, data?.[TRADE_PARTNER_COUNTRY_CODE], preselectedState]
  )

  useEffect(() => {
    if (discardChangeCounter) {
      setSelectedCompany(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discardChangeCounter])

  const resetCompanyAddress = useCallback(
    (shouldResetState = true) => {
      changeData(TRADE_PARTNER_COMPANY_NAME, undefined)
      changeData(TRADE_PARTNER_STREET_NAME, undefined)
      changeData(TRADE_PARTNER_STREET_NUMBER, undefined)
      changeData(TRADE_PARTNER_CITY, undefined)
      changeData(TRADE_PARTNER_ZIP_CODE, undefined)
      changeData(TRADE_PARTNER_LEGAL_FORM, undefined)
      changeData(TRADE_PARTNER_WEBSITE, undefined)
      shouldResetState && changeData(TRADE_PARTNER_STATE, undefined)
      changeData(TRADE_PARTNER_TRADING_CURRENCY, undefined)
      changeData(TRADE_PARTNER_EXPECTED_FINANCING_LIMIT, undefined)
    },
    [changeData]
  )

  useEffect(() => {
    // Explicity checking for selectedCountryCode change instead of relying on dependencies array
    // since the whole component is rendering again even on state or other field changes
    // @todo NEED TO FIX/REWRITE THIS COMPONENT LATER
    if (selectedCountryCode && selectedCountryCode !== countryRef.current) {
      resetCompanyAddress()
      changeData('tradePartner.companyNameSearchSelect', undefined)
      onChange && onChange({dataEntry: 'SEARCH', valueSelected: false})
      setSelectedCompany(false)
      countryRef.current = selectedCountryCode
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode])

  useEffect(() => {
    // if (country code is US or CA & state is changed then reset address)
    if (selectedCountryCode && ['US', 'CA'].includes(selectedCountryCode)) {
      resetCompanyAddress(false)
      changeData('tradePartner.companyNameSearchSelect', undefined)
      onChange && onChange({dataEntry: 'SEARCH', valueSelected: false})
      setSelectedCompany(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedState])

  const handleOnChange = useCallback(
    (updatedValue: any) => {
      setSelectedCompany(updatedValue)
      const resultData = updatedValue?.suggestion || updatedValue
      changeData(TRADE_PARTNER_COMPANY_NAME, resultData?.companyName)
      changeData(TRADE_PARTNER_STREET_NAME, resultData?.address?.streetName)
      changeData(TRADE_PARTNER_STREET_NUMBER, resultData?.address?.streetNumber)
      changeData(TRADE_PARTNER_CITY, resultData?.address?.city)
      if (selectedCountryCode && !['US', 'CA'].includes(selectedCountryCode as string)) {
        changeData(TRADE_PARTNER_STATE, resultData?.address?.state)
      }

      changeData(TRADE_PARTNER_ZIP_CODE, resultData?.address?.zipCode)
      if (resultData?.legalForm) {
        changeData(TRADE_PARTNER_LEGAL_FORM, resultData?.legalForm)
      } else if (getLegalForms(selectedCountryCode as string)?.length === 1) {
        // prefilling legal form value when only one option is available
        changeData(TRADE_PARTNER_LEGAL_FORM, getLegalForms(selectedCountryCode as string)?.[0])
      }

      changeData(TRADE_PARTNER_WEBSITE, resultData?.website)
      onChange &&
        onChange({
          valueSelected: updatedValue,
          hasLegalFormValue:
            !!resultData?.legalForm || getLegalForms(selectedCountryCode as string)?.length === 1,
          hasWebsiteValue: !!resultData?.website,
        })
    },
    [onChange, changeData, selectedCountryCode, getLegalForms]
  )

  const handleAddNewCompany = useCallback(() => {
    resetCompanyAddress()
    if (getLegalForms(selectedCountryCode as string)?.length === 1) {
      changeData(TRADE_PARTNER_LEGAL_FORM, getLegalForms(selectedCountryCode as string)?.[0])
    }
    openBuyerSearchDialog({
      selectedCountryCode,
      onSubmit: handleOnChange,
    })
  }, [
    resetCompanyAddress,
    getLegalForms,
    selectedCountryCode,
    openBuyerSearchDialog,
    handleOnChange,
    changeData,
  ])

  const handleAllowSellerSearch = useCallback(() => {
    setSelectedCompany(false)
    resetCompanyAddress()
    onChange && onChange({dataEntry: 'SEARCH', valueSelected: false})
  }, [onChange, resetCompanyAddress])

  const getCompanyNameLabel = useCallback(
    (isDisabled?: boolean) => (
      <CompanyLabelWrapper>
        <span>
          {t(
            [
              `company-name.label.${selectedCountryCode ? selectedCountryCode?.toLowerCase() : ''}`,
              `company-name.label`,
            ],
            {ns: 'plugin-forms'}
          )}
        </span>
        <StyledButtonLink
          id={
            selectedCompany
              ? 'search-new-buyer-company'
              : 'search-buyer-company-by-registration-number'
          }
          data-target="company-search"
          small
          icon
          onClick={selectedCompany ? handleAllowSellerSearch : handleAddNewCompany}
          data-ident={
            selectedCompany
              ? 'search-new-buyer-company'
              : 'search-buyer-company-by-registration-number'
          }
          disabled={!!isDisabled}
        >
          {t(
            `trade-partner-page.${
              selectedCompany ? 'company-name-search' : 'search-by-registration'
            }.cta`
          )}
        </StyledButtonLink>
      </CompanyLabelWrapper>
    ),
    [handleAllowSellerSearch, selectedCountryCode, t, handleAddNewCompany, selectedCompany]
  )

  const handleInputValueChange = useCallback(() => {
    setCompanySearchError('')
  }, [])

  const isDisabled =
    !!selectedCountryCode && (highBuyerRiskCountries as string[])?.includes(selectedCountryCode)

  return (
    <>
      {!selectedCompany && (
        <AutoSuggestWrapper>
          <FormInput
            inputComponent={SearchInputWithContainer}
            t={t}
            name="tradePartner.companyNameSearchSelect"
            label={getCompanyNameLabel(isDisabled)}
            placeholder={t('trade-partner-page.company-name-search-select.placeholder')}
            autoComplete="off"
            async
            performSearch={loadNewOptions}
            suggestionComponent={Suggestion}
            transformSuggestion={({companyName}: {companyName: string}) => companyName}
            onSelect={handleOnChange}
            onChange={handleInputValueChange}
            suggestionContainerFactory={SuggestionContainerFactory}
            required
            disabled={isDisabled}
            isValid={!companySearchError}
            errorMessage={companySearchError}
            hasResults={hasResults}
            noResultsMessage={t('trade-partner-page.company-name-search-select.no-results-found')}
          />
        </AutoSuggestWrapper>
      )}
      {!!selectedCompany && (
        <FormInput
          t={t}
          name={TRADE_PARTNER_COMPANY_NAME}
          inputComponent={InputPureWithInputContainer}
          label={getCompanyNameLabel(isDisabled)}
          disabled={!!selectedCompany}
          required
          {...restProps}
        />
      )}
    </>
  )
}

export default CompanyNameSearch
